import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown, FiChevronUp, FiZap, FiDollarSign, FiUsers, FiImage, FiVideo, FiMic, FiGlobe, FiCpu, FiTrendingUp } = FiIcons;

const Features = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const features = [
    {
      title: "Affordable Creativity Tools",
      description: "Unlock cutting-edge AI tools to unleash your creative potential without breaking the bank. Lock in your price now before prices increase!",
      icon: FiDollarSign,
      color: "from-green-400 to-blue-500"
    },
    {
      title: "Comprehensive Business Solutions",
      description: "Elevate your business operations with AI-driven tools designed to streamline processes and enhance productivity.",
      icon: FiCpu,
      color: "from-blue-400 to-purple-500"
    },
    {
      title: "Enhanced Marketing Strategies",
      description: "Transform your marketing with AI-enhanced features that deliver impactful results and boost brand visibility.",
      icon: FiTrendingUp,
      color: "from-purple-400 to-pink-500"
    },
    {
      title: "Professional Image Creation",
      description: "Create stunning, professional-quality visuals effortlessly with advanced AI image tools.",
      icon: FiImage,
      color: "from-pink-400 to-red-500"
    },
    {
      title: "Innovative Content Creation",
      description: "Generate engaging content from AI music to dynamic videos, all on one platform from idea to finished product.",
      icon: FiVideo,
      color: "from-yellow-400 to-orange-500"
    },
    {
      title: "Revenue Generation Opportunities",
      description: "Utilize our affiliate program and commerce tools to explore new revenue streams.",
      icon: FiUsers,
      color: "from-cyan-400 to-blue-500"
    }
  ];

  const planFeatures = {
    playground: [
      "Deep Research", "Hero Images", "AI Image Editor", "Hyperrealistic AI Images",
      "Conversational Images", "AI Videos", "Movie Editor", "Audiobook Maker",
      "AI Voice Over", "AI Smart Websites", "AEO Blogs", "AI Music Generator",
      "Next Level Creator", "And More!"
    ],
    business: [
      "Everything in Playground +", "Logo Maker", "AI Spokesperson", "Easy CRM",
      "Business Phone", "AI Podcasts", "Magic Hooks", "Email Marketing",
      "AI Forms", "TaskFlow", "Invoice Generator", "Super Agent",
      "AI App Wizard", "And 20+ More Tools!"
    ],
    marketer: [
      "Everything in Business +", "White Label Version", "Custom AI Apps",
      "Academy Builder", "External App Integration", "AI Personalities",
      "Live Training Platform", "Your Own AI Assistant Brand!"
    ]
  };

  return (
    <section id="features" className="py-24 bg-gray-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-500">NextLevel</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Stop paying for 10 different subscriptions. Get everything you need in ONE powerful platform!
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative"
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300`} />
              <div className="relative bg-gray-800 border border-gray-700 rounded-2xl p-8 hover:border-gray-600 transition-all duration-300">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Accordion Plans */}
        <div className="max-w-5xl mx-auto">
          <h3 className="text-4xl font-black text-white text-center mb-12">
            3 Plan Levels For Your Needs
          </h3>

          {[
            { name: "Playground", price: "$29", features: planFeatures.playground, color: "from-green-400 to-blue-500" },
            { name: "Business", price: "$49", features: planFeatures.business, color: "from-blue-400 to-purple-500" },
            { name: "Marketer", price: "$79", features: planFeatures.marketer, color: "from-purple-400 to-pink-500" }
          ].map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="mb-6"
            >
              <motion.button
                onClick={() => toggleSection(plan.name)}
                className={`w-full p-6 bg-gradient-to-r ${plan.color} rounded-2xl text-left flex items-center justify-between group`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-black text-white">{plan.price}</span>
                  <span className="text-xl font-bold text-white">{plan.name}</span>
                </div>
                <SafeIcon icon={expandedSection === plan.name ? FiChevronUp : FiChevronDown} className="w-6 h-6 text-white" />
              </motion.button>

              <AnimatePresence>
                {expandedSection === plan.name && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 bg-gray-800 border border-gray-700 rounded-b-2xl">
                      <div className="grid md:grid-cols-2 gap-4">
                        {plan.features.map((feature, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="flex items-center gap-3"
                          >
                            <SafeIcon icon={FiZap} className="w-5 h-5 text-yellow-400" />
                            <span className="text-gray-300">{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <motion.a
            href="https://buy.stripe.com/4gMeVe5Ep3Ml3CK9bk6Vq04"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-12 py-6 bg-gradient-to-r from-yellow-400 to-pink-500 text-black text-xl font-black rounded-full transform transition-all duration-300 hover:scale-110"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Free Trial Now! 🚀
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;